import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Button, Chip, Stack, Tab, Tabs, TextField, Toolbar, Typography, alpha,
} from '@mui/material';
import BalanceIcon from '@mui/icons-material/Balance';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useNotification } from '@1823-partners/core';

import UploadView from './UploadView';
import ReconTable from './ReconTable';
import RawTable from './RawTable';
import BreaksView from './BreaksView';
import CashRecView from './CashRecView';
import AggregationView from './AggregationView';
import ReferencePositions from './ReferencePositions';
import { applyDateFilter } from '../../utils/derivativeReconFormat';
import { downloadDerivativeReconExport, runDerivativeRecon } from '../../api/derivativeRecon';

const RECON_TABS = ['Positions', 'Premiums', 'Payoffs', 'Breaks', 'Cash Rec', 'Aggregation'];
const RAW_TABS = ['Milliman Positions', 'Milliman Payoffs', 'CW Positions', 'CW Transactions'];
const REF_TAB = 'Reference Positions';
const ALL_TABS = [...RECON_TABS, ...RAW_TABS, REF_TAB];

const EMPTY_FILES = { mil1: null, mil2: null, cwPos: null, cwTx: null, ntFile: null, jpmFile: null };

/**
 * Fold a Cash Rec result payload into the recon state.
 *
 * `??` falls through on null, so reading `payload?.cashResults ?? payload`
 * stored the whole payload object when Cash Rec cleared its results — the
 * recon grids then threw trying to iterate it. Callers may also pass the
 * results array directly, hence the isArray branch.
 */
export function applyCashResults(prev, payload) {
  if (!prev) return prev;
  return {
    ...prev,
    cashResults: Array.isArray(payload) ? payload : payload?.cashResults ?? null,
    wires: payload?.wires ?? prev.wires ?? {},
    tradeWireIndex: payload?.tradeWireIndex ?? prev.tradeWireIndex ?? {},
  };
}

export default function DerivativeReconDashboard() {
  const { showNotification } = useNotification();
  const [files, setFiles] = useState(EMPTY_FILES);
  const [recon, setRecon] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('Positions');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const onRunRecon = useCallback(async () => {
    if (!(files.mil1 && files.mil2 && files.cwPos && files.cwTx)) return;
    setLoading(true);
    setError(null);
    try {
      const result = await runDerivativeRecon({
        mil1: files.mil1, mil2: files.mil2,
        cwPos: files.cwPos, cwTx: files.cwTx,
        ntFile: files.ntFile, jpmFile: files.jpmFile,
      });
      setRecon(result);
      setActiveTab('Positions');
      showNotification(`Reconciled ${result.posResults?.length ?? 0} positions`, 'success');
    } catch (ex) {
      setError(ex.message || String(ex));
      showNotification(`Reconciliation failed: ${ex.message}`, 'error');
    }
    setLoading(false);
  }, [files, showNotification]);

  const reset = () => {
    setRecon(null);
    setFiles(EMPTY_FILES);
    setActiveTab('Positions');
    setError(null);
  };

  const handleExport = () => {
    try {
      downloadDerivativeReconExport(recon);
      showNotification('Excel export downloaded', 'success');
    } catch (ex) {
      showNotification(`Export failed: ${ex.message}`, 'error');
    }
  };

  const filtered = useMemo(() => {
    if (!recon) return null;
    return {
      Positions: applyDateFilter(recon.posResults || [], 'milTrade', dateFrom, dateTo),
      Premiums: applyDateFilter(recon.premResults || [], 'settle', dateFrom, dateTo),
      Payoffs: applyDateFilter(recon.payResults || [], 'settle', dateFrom, dateTo),
    };
  }, [recon, dateFrom, dateTo]);

  if (!recon) {
    // Reference Positions is Snowflake-backed and independent of the upload
    // flow, so it stays reachable before any files are loaded.
    if (activeTab === REF_TAB) {
      return (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Button size="small" variant="outlined" onClick={() => setActiveTab('Positions')}>
              ← Back to Upload
            </Button>
            <Typography variant="subtitle1" fontWeight={700}>Reference Positions (Snowflake)</Typography>
          </Stack>
          <ReferencePositions />
        </Box>
      );
    }
    return (
      <>
        <Toolbar variant="dense" disableGutters sx={{ gap: 1, mb: 1 }}>
          <BalanceIcon sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight={800}>Derivative Reconciliation</Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" onClick={() => setActiveTab(REF_TAB)}>
            Reference Positions
          </Button>
        </Toolbar>
        <UploadView
          files={files}
          setFiles={setFiles}
          onRun={onRunRecon}
          loading={loading}
          error={error}
          setError={setError}
        />
      </>
    );
  }

  const breakCount = (rows) => (rows ? rows.filter((r) => r.status !== 'MATCHED').length : 0);
  const totalBreaks =
    breakCount(filtered.Positions) + breakCount(filtered.Premiums) + breakCount(filtered.Payoffs);

  const tabBadge = (t) => {
    if (t === 'Breaks') {
      if (!totalBreaks) return null;
      return <Chip size="small" label={`${totalBreaks} total`} sx={{ ml: 1, height: 18, fontSize: 10, fontWeight: 700, bgcolor: alpha('#f87171', 0.18), color: '#f87171' }} />;
    }
    if (['Positions', 'Premiums', 'Payoffs'].includes(t)) {
      const arr = filtered[t];
      if (!arr || !arr.length) return <Chip size="small" label="no data" sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: 'action.hover', color: 'text.secondary' }} />;
      const breaks = breakCount(arr);
      return <Chip size="small" label={breaks ? `${breaks} break${breaks > 1 ? 's' : ''}` : '✓ clean'} sx={{ ml: 1, height: 18, fontSize: 10, fontWeight: 700, bgcolor: alpha(breaks ? '#f87171' : '#34d399', 0.18), color: breaks ? '#f87171' : '#34d399' }} />;
    }
    return null;
  };

  const wireProps = {
    wires: recon.wires || {},
    tradeWireIndex: recon.tradeWireIndex || {},
    cashResults: recon.cashResults || [],
  };

  const renderActive = () => {
    if (activeTab === 'Positions') return <ReconTable results={recon.posResults || []} type="Positions" dateFrom={dateFrom} dateTo={dateTo} />;
    if (activeTab === 'Premiums') return <ReconTable results={recon.premResults || []} type="Premiums" dateFrom={dateFrom} dateTo={dateTo} {...wireProps} />;
    if (activeTab === 'Payoffs') return <ReconTable results={recon.payResults || []} type="Payoffs" dateFrom={dateFrom} dateTo={dateTo} {...wireProps} />;
    if (activeTab === 'Breaks') return <BreaksView posResults={filtered.Positions} premResults={filtered.Premiums} payResults={filtered.Payoffs} dateFrom={dateFrom} dateTo={dateTo} {...wireProps} />;
    if (activeTab === 'Cash Rec') {
      return (
        <CashRecView
          flows={recon.milFlows || []}
          cwTxRows={recon.cwTxRaw || []}
          initialResults={recon.cashResults || null}
          onResults={(payload) => setRecon((prev) => applyCashResults(prev, payload))}
        />
      );
    }
    if (activeTab === 'Aggregation') return <AggregationView posResults={recon.posResults || []} />;
    if (activeTab === 'Milliman Positions') return <RawTable rows={recon.milRaw || []} />;
    if (activeTab === 'Milliman Payoffs') return <RawTable rows={recon.milPayRaw || []} />;
    if (activeTab === 'CW Positions') return <RawTable rows={recon.cwRaw || []} />;
    if (activeTab === 'CW Transactions') return <RawTable rows={recon.cwTxRaw || []} />;
    if (activeTab === REF_TAB) return <ReferencePositions />;
    return null;
  };

  return (
    <Box>
      {/* Toolbar */}
      <Toolbar variant="dense" disableGutters sx={{ gap: 1, mb: 1 }}>
        <BalanceIcon sx={{ color: 'primary.main' }} />
        <Typography variant="subtitle1" fontWeight={800}>Derivative Reconciliation</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" color="success" startIcon={<DownloadIcon />} onClick={handleExport}>
          Export to Excel
        </Button>
        <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} onClick={reset}>
          New Files
        </Button>
      </Toolbar>

      {/* Date range filter */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1.5, mb: 0, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">Date Range:</Typography>
        <TextField type="date" size="small" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
        <Typography variant="caption" color="text.secondary">to</Typography>
        <TextField type="date" size="small" value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
        {(dateFrom || dateTo) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" variant="outlined" color="error" onClick={() => { setDateFrom(''); setDateTo(''); }}>✕ Clear</Button>
            <Typography variant="caption" color="primary" fontWeight={600}>Positions (Trade Dt), Premiums &amp; Payoffs (Settle Dt)</Typography>
          </Stack>
        )}
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        {ALL_TABS.map((t) => (
          <Tab key={t} value={t}
            label={<Box sx={{ display: 'flex', alignItems: 'center' }}><span>{t}</span>{tabBadge(t)}</Box>}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13 }}
          />
        ))}
      </Tabs>

      <Box sx={{ pt: 2 }}>{renderActive()}</Box>
    </Box>
  );
}
